import { formatCreditos } from '../lib/creditos.js';
import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Field,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tag,
  Button,
} from '@fluentui/react-components';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { useAuthStore } from '../stores/authStore.js';
import {
  getStaffUserUsage,
  listStaffTenantEvents,
  type UserDetailResult,
  type RawUsageEventsPage,
} from '../api/staffUsage.js';
import { ApiClientError } from '../api/client.js';
import {
  formatUsd,
  fmtDateTime,
  monthRangeFromInput,
  todayIsoEndOfDay,
  monthsAgoIso,
} from '../lib/staffUsageFormat.js';
import { MonthlyBarChart } from '../components/MonthlyBarChart.js';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  content: { flex: 1, padding: '24px 32px', maxWidth: '1280px', width: '100%', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' },
  back: { color: tokens.colorBrandForegroundLink, textDecoration: 'none', fontSize: tokens.fontSizeBase300 },
  title: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold },
  subtitle: { color: tokens.colorNeutralForeground3, marginBottom: '20px' },
  filters: { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '20px' },
  filterField: { minWidth: '160px' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' },
  kpiCard: { padding: '14px 18px', background: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  kpiLabel: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginBottom: '4px' },
  kpiValue: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold, fontVariantNumeric: 'tabular-nums' },

  panel: { background: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: '16px 20px', marginBottom: '20px' },
  panelTitle: { fontWeight: tokens.fontWeightSemibold, marginBottom: '12px' },

  twoCol: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '20px', marginBottom: '24px', '@media (max-width: 900px)': { gridTemplateColumns: '1fr' } },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  thRight: { textAlign: 'right' },
  td: { padding: '8px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, fontSize: tokens.fontSizeBase300 },
  tdRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  pagerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' },
});

const FEATURE_LABEL: Record<string, string> = {
  chat: 'Chat',
  summary: 'Resumen',
  quote: 'Cotización',
  apu_ia: 'APU-IA',
  embedding: 'Embeddings',
  web_search: 'Búsqueda web',
  vision: 'Lectura de imágenes',
  other: 'Otro',
};

const EVENTS_LIMIT = 50;

export function StaffUserUsagePage() {
  const styles = useStyles();
  const { tenantId, userId } = useParams<{ tenantId: string; userId: string }>();
  const tenantInAuth = useAuthStore((s) => s.tenant);
  const isStaff =
    !!tenantInAuth?.isEcosoftOwned &&
    (tenantInAuth.role === 'admin' || tenantInAuth.role === 'owner');

  const [from, setFrom] = useState<string>(() => monthsAgoIso(11));
  const [to, setTo] = useState<string>(() => todayIsoEndOfDay());
  const [data, setData] = useState<UserDetailResult | null>(null);
  const [events, setEvents] = useState<RawUsageEventsPage | null>(null);
  const [eventsOffset, setEventsOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isStaff || !tenantId || !userId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getStaffUserUsage(tenantId, userId, {
      from: monthRangeFromInput(from, 'start'),
      to: monthRangeFromInput(to, 'end'),
    })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiClientError ? e.message : 'Error al cargar usuario');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, tenantId, userId, from, to]);

  useEffect(() => {
    if (!isStaff || !tenantId || !userId) return;
    let cancelled = false;
    setLoadingEvents(true);
    listStaffTenantEvents(tenantId, {
      userId,
      from: monthRangeFromInput(from, 'start'),
      to: monthRangeFromInput(to, 'end'),
      limit: EVENTS_LIMIT,
      offset: eventsOffset,
    })
      .then((res) => {
        if (!cancelled) setEvents(res);
      })
      .catch(() => {
        // No-op — errores aquí no rompen el resto
      })
      .finally(() => {
        if (!cancelled) setLoadingEvents(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, tenantId, userId, from, to, eventsOffset]);

  if (!isStaff) return <Navigate to="/" replace />;

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs
        items={[
          { label: 'Inicio', to: '/projects' },
          { label: 'Staff' },
          { label: 'Consumo por tenant', to: '/staff/usage' },
          { label: 'Usuario' },
        ]}
      />
      <main className={styles.content}>
        <div className={styles.header}>
          <Link to={`/staff/usage/tenants/${tenantId}`} className={styles.back}>← Volver al tenant</Link>
          <Text as="h1" className={styles.title}>
            {data?.user.email ?? '…'}
          </Text>
          {data?.user.role && <Tag size="small">{data.user.role}</Tag>}
        </div>
        {data?.user.name && <div className={styles.subtitle}>{data.user.name}</div>}

        <div className={styles.filters}>
          <Field label="Desde" className={styles.filterField}>
            <Input type="date" value={from.slice(0, 10)} onChange={(_e, d) => { setFrom(d.value); setEventsOffset(0); }} />
          </Field>
          <Field label="Hasta" className={styles.filterField}>
            <Input type="date" value={to.slice(0, 10)} onChange={(_e, d) => { setTo(d.value); setEventsOffset(0); }} />
          </Field>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: 16 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {loading && <Spinner size="small" label="Cargando…" />}

        {!loading && data && (
          <>
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>$ período</div>
                <div className={styles.kpiValue}>{formatUsd(data.user.periodCostUsd)}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>$ anual (ciclo suscripción)</div>
                <div className={styles.kpiValue}>{formatUsd(data.user.annualCostUsd)}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Turnos LLM</div>
                <div className={styles.kpiValue}>{data.user.periodTurns.toLocaleString('es-MX')}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiLabel}>Feature top</div>
                <div className={styles.kpiValue} style={{ fontSize: tokens.fontSizeBase400 }}>
                  {data.byFeature[0] ? FEATURE_LABEL[data.byFeature[0].key] ?? data.byFeature[0].key : '—'}
                </div>
              </div>
            </div>

            <div className={styles.panel}>
              <div className={styles.panelTitle}>Costo mensual (USD)</div>
              <MonthlyBarChart data={data.monthly} />
            </div>

            <div className={styles.twoCol}>
              <div className={styles.panel} style={{ margin: 0 }}>
                <div className={styles.panelTitle}>Por modelo</div>
                <BreakdownTable rows={data.byModel} />
              </div>
              <div className={styles.panel} style={{ margin: 0 }}>
                <div className={styles.panelTitle}>Por feature</div>
                <BreakdownTable
                  rows={data.byFeature.map((r) => ({ ...r, key: FEATURE_LABEL[r.key] ?? r.key }))}
                />
              </div>
            </div>

            <div className={styles.panel}>
              <div className={styles.panelTitle}>
                Eventos (drill-down) {events && `· ${events.total.toLocaleString('es-MX')} total`}
              </div>
              {loadingEvents && <Spinner size="tiny" label="Cargando eventos…" />}
              {!loadingEvents && events && events.items.length === 0 && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Sin eventos.</Text>
              )}
              {!loadingEvents && events && events.items.length > 0 && (
                <>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>Fecha</th>
                        <th className={styles.th}>Feature</th>
                        <th className={styles.th}>Modelo</th>
                        <th className={`${styles.th} ${styles.thRight}`}>Input</th>
                        <th className={`${styles.th} ${styles.thRight}`}>Output</th>
                        <th className={`${styles.th} ${styles.thRight}`} title="Consumo real ÷ 10,000 (sin factor)">Créditos (real)</th>
                        <th className={`${styles.th} ${styles.thRight}`}>$</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.items.map((e) => (
                        <tr key={e.id}>
                          <td className={styles.td}>{fmtDateTime(e.occurredAt)}</td>
                          <td className={styles.td}>{FEATURE_LABEL[e.feature] ?? e.feature}</td>
                          <td className={styles.td}>{e.model ?? '—'}</td>
                          <td className={`${styles.td} ${styles.tdRight}`}>{(e.inputTokens ?? 0).toLocaleString('es-MX')}</td>
                          <td className={`${styles.td} ${styles.tdRight}`}>{(e.outputTokens ?? 0).toLocaleString('es-MX')}</td>
                          <td className={`${styles.td} ${styles.tdRight}`}>{formatCreditos((e.inputTokens ?? 0) + (e.outputTokens ?? 0), 'desglose')}</td>
                          <td className={`${styles.td} ${styles.tdRight}`}>{formatUsd(e.costUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className={styles.pagerRow}>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      Mostrando {events.offset + 1}–{Math.min(events.offset + events.items.length, events.total)} de {events.total.toLocaleString('es-MX')}
                    </Text>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        size="small"
                        disabled={eventsOffset === 0}
                        onClick={() => setEventsOffset(Math.max(0, eventsOffset - EVENTS_LIMIT))}
                      >
                        Anterior
                      </Button>
                      <Button
                        size="small"
                        appearance="primary"
                        disabled={eventsOffset + EVENTS_LIMIT >= events.total}
                        onClick={() => setEventsOffset(eventsOffset + EVENTS_LIMIT)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function BreakdownTable({ rows }: { rows: { key: string; costUsd: number; turns: number }[] }) {
  const styles = useStyles();
  if (rows.length === 0) {
    return <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Sin datos.</Text>;
  }
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.th}></th>
          <th className={`${styles.th} ${styles.thRight}`}>$</th>
          <th className={`${styles.th} ${styles.thRight}`}>Turnos</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td className={styles.td}>{r.key}</td>
            <td className={`${styles.td} ${styles.tdRight}`}>{formatUsd(r.costUsd)}</td>
            <td className={`${styles.td} ${styles.tdRight}`}>{r.turns.toLocaleString('es-MX')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
